import { ArrowBack, ContentCopy, Sync } from "@mui/icons-material"
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { LockerVitals } from "./saLockerVitals"

const Fact: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Stack direction="column" spacing={0.25} sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Box>{children}</Box>
  </Stack>
)

/**
 * Identity + at-a-glance condition for the locker, above the working columns.
 *
 * Plain MUI surface on purpose: this page sits beside every other SA screen, and
 * a bespoke visual treatment here reads as a different product rather than as
 * emphasis.
 */
export const SaLockerConsoleHeader: FC<{
  name: string
  lockerId: string
  tenantName?: string
  tenantId?: string
  isLoadingTenant?: boolean
  vitals: LockerVitals
  isVirtual?: boolean
  onSync: () => void
  isSyncing: boolean
}> = ({
  name,
  lockerId,
  tenantName,
  tenantId,
  isLoadingTenant,
  vitals,
  isVirtual,
  onSync,
  isSyncing,
}) => {
  const { t } = useTranslation()

  const onlineColor =
    vitals.online === null ? "text.disabled" : vitals.online ? "success.main" : "error.main"
  const onlineLabel =
    vitals.online === null
      ? t("saLockerConsole.unknown")
      : vitals.online
        ? t("online")
        : t("offline")

  return (
    <Paper data-ui="UI-SL2W-143" variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
      {/* Breadcrumb */}
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1.5 }}>
        <IconButton component={Link} to="/sa/lockers" size="small" aria-label={t("lockers")}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography
          component={Link}
          to="/sa/lockers"
          variant="body2"
          color="text.secondary"
          sx={{ textDecoration: "none" }}
        >
          {t("lockers")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          /
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {name}
        </Typography>
      </Stack>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 2, md: 4 }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "flex-end" }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
            <Typography variant="h5" fontWeight={700} sx={{ overflowWrap: "anywhere" }}>
              {name}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={`SST ${vitals.apiVersion.toUpperCase()}`}
              color={vitals.apiVersion === "v2" ? "info" : "default"}
            />
            {isVirtual && (
              <Chip size="small" variant="outlined" color="secondary" label={t("virtual")} />
            )}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: onlineColor,
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" sx={{ color: onlineColor }}>
              {onlineLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ·
            </Typography>
            <Typography variant="caption" color="text.secondary" fontFamily="monospace">
              {lockerId}
            </Typography>
            <Tooltip title={t("copy")}>
              <IconButton
                size="small"
                sx={{ p: 0.25 }}
                onClick={() => navigator.clipboard?.writeText(lockerId)}
                aria-label={t("copy")}
              >
                <ContentCopy sx={{ fontSize: 12 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Stack
          direction="row"
          spacing={3}
          alignItems="flex-end"
          sx={{ flexShrink: 0, flexWrap: "wrap", rowGap: 1.5 }}
        >
          <Fact label={t("tenant")}>
            {isLoadingTenant ? (
              <Skeleton width={110} height={20} />
            ) : tenantId ? (
              <Typography
                component={Link}
                to={`/sa/tenants/${tenantId}`}
                variant="body2"
                fontWeight={600}
                color="primary"
                sx={{ textDecoration: "none" }}
              >
                {tenantName || "—"}
              </Typography>
            ) : (
              <Typography variant="body2">—</Typography>
            )}
          </Fact>

          <Fact label={t("saLockerConsole.serial")}>
            <Typography variant="body2" fontFamily="monospace">
              {vitals.serialNumber || "—"}
            </Typography>
          </Fact>

          <Fact label={t("saLockerConsole.doors")}>
            <Typography variant="body2">
              <Box component="span" fontWeight={700}>
                {vitals.doorOccupied}
              </Box>
              <Box component="span" color="text.secondary">
                {/* Denominator is ALL doors, matching the dashboard's occupancy
                    percentage directly below. Two different denominators on one
                    page is a bug report waiting to happen. */}
                {` / ${vitals.doorTotal} ${t("saLockerConsole.inUse")}`}
              </Box>
            </Typography>
          </Fact>

          <Tooltip title={t("saLockerConsole.syncFromSst")}>
            <span>
              <IconButton
                onClick={onSync}
                disabled={isSyncing || !vitals.hasSnapshot}
                size="small"
                aria-label={t("saLockerConsole.syncFromSst")}
              >
                {isSyncing ? <CircularProgress size={16} /> : <Sync fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  )
}

export default SaLockerConsoleHeader
